import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, Divider, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { GreenText, LoadingContent } from 'components'
import * as controllers from 'controllers'
import { VehicleDto, VehicleInvoicingDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { Fragment, useEffect, useState } from 'react'
import { formatCurrency } from 'utils'
import InvoicingDetailsAddEditDialog from './InvoicingDetailsAddEditDialog'

interface InvoicingDetailsCardProps {
  vehicle: VehicleDto
}

export default function InvoicingDetailsCard({ vehicle }: InvoicingDetailsCardProps) {
  const [isSavingInvoicingDetails, setIsSavingInvoicingDetails] = useState<boolean>(false)
  const [isGettingInvoicingDetails, setIsGettingInvoicingDetails] = useState<boolean>(false)
  const [invoicingDetailsAddEditDialogOpen, setInvoicingDetailsAddEditDialogOpen] = useState<boolean>(false)
  const [initialValues, setInitialValues] = useState<VehicleInvoicingDto>(new VehicleInvoicingDto())

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsGettingInvoicingDetails(true)
      controllers.vehicles
        .getVehicleInvoicing(vehicle.vehicleGuid!)
        .then(res => setInitialValues(res.value))
        .finally(() => setIsGettingInvoicingDetails(false))
    }
  }, [vehicle])

  return (
    <>
      <InvoicingDetailsAddEditDialog
        initialValues={initialValues}
        isLoading={isSavingInvoicingDetails}
        onClose={() => setInvoicingDetailsAddEditDialogOpen(false)}
        onSave={values => {
          setIsSavingInvoicingDetails(true)
          controllers.vehicles
            .updateVehicleInvoicing(vehicle.vehicleGuid!, values)
            .then(response => {
              setInitialValues(response.value)
              setInvoicingDetailsAddEditDialogOpen(false)
              enqueueSnackbar('Invoice Information Saved Successfully!', { variant: 'success' })
            })
            .finally(() => setIsSavingInvoicingDetails(false))
        }}
        open={invoicingDetailsAddEditDialogOpen}
      />

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography variant='h5' fontWeight='bold'>
                  Invoicing
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  disabled={Boolean(isGettingInvoicingDetails || initialValues.quickBooksInvoiceNumber)}
                  endIcon={<Edit />}
                  onClick={() => setInvoicingDetailsAddEditDialogOpen(true)}
                  variant='text'
                >
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <LoadingContent isLoading={isGettingInvoicingDetails}>
              <Grid item xs={12} container spacing={1}>
                {[
                  { label: 'Import Charge', key: 'invoicingImportCharge' },
                  { label: 'Duty', key: 'invoicingDuty' },
                  { label: 'Inbound Transportation', key: 'invoicingInboundTransportation' },
                  { label: 'Outbound Transportation', key: 'invoicingOutboundTransportation' },
                  { label: 'Cluster Conversion', key: 'invoicingClusterConversion' },
                  { label: 'Cluster Programming', key: 'invoicingClusterProgramming' },
                  { label: 'Cluster Replacement', key: 'invoicingClusterReplacement' },
                  { label: 'Title Fees', key: 'invoicingTitleFees' },
                  { label: 'Recall Expenses', key: 'invoicingRecallExpenses' },
                  { label: 'FedEx', key: 'invoicingFedEx' },
                  { label: 'FedEx API', key: 'invoicingFedExAPI' },
                  { label: 'Replacement Label', key: 'invoicingReplacementLabel' },
                  { label: 'Repairs', key: 'invoicingRepairs' },
                  { label: 'Vehicle Clean Up', key: 'invoicingVehicleCleanUp' },
                  { label: 'Miscellaneous', key: 'invoicingMiscellaneous' }
                ].map(({ label, key }) => (
                  <Fragment key={key}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='body1'>{label}</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant='body1' textAlign='right'>
                        {formatCurrency(Number(initialValues[key as keyof VehicleInvoicingDto] ?? 0))}
                      </Typography>
                    </Grid>
                  </Fragment>
                ))}

                <Grid item xs={12} sm={6}>
                  <Typography variant='body1' fontWeight='bold'>
                    TOTAL
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant='body1' fontWeight='bold' textAlign='right'>
                    {formatCurrency(initialValues.invoicingTotal)}
                  </Typography>
                </Grid>
              </Grid>
            </LoadingContent>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`As transactions are added to the Transactions table, the Amount associated with that transaction is added to or deducted from the corresponding “Service Name” on the “Invoicing” card. Note: When this process 
occurs, it must only add or deduct the amount from the new transaction to the existing value on the Invoicing card (it cannot recalculate all transactions with a type/service name due to user’s ability to manually edit 
the values on the Invoicing card).

When invoicing data is sent from VTS2 to QuickBooks, the following QuickBooks account categories and sub-categories will be used:

VTS2 Invoice Service Name | QuickBooks Account Category | QuickBooks Account Sub-Category

   - Import Charge | 100 - Importing Income | 101 - Sales-Importing Fees
   - Duty | 100 - Importing Income | 114 - Duty
   - Inbound Transportation | 106 - Transportation | 106.1 - Incoming transportation
   - Cluster Conversion | 102 - Sales - Speedometer Conversions
   - Cluster Programming | 102 - Sales - Speedometer Conversions
   - Cluster Replacement | 102- Sales - Speedometer Conversions
   - Title Fees | 103 - Sales-Title Work
   - Recall Expenses | 100 - Importing Income | 101.1 - Recall Repair
   - FedEx | 110 - Miscellaneous Income | 191 - FedEx & USPS
   - FedEx API | 110 - Miscellaneous Income | 191 - FedEx & USPS
   - Replacement Label | 110 - Miscellaneous Income | 115 - Manufacturing Labels
   - Repairs | 110 - Miscellaneous Income | 107 - Repair-Sublet
   - Vehicle Clean Up | 110 - Miscellaneous Income | 113 - Income-Clean Up Fees
   - Miscellaneous | 110 - Miscellaneous Income
   - Outbound Transportation | 106 - Transportation | 106.2 - Outbound Transportation

When the “QuickBooks Invoice #” field contains a value, the Edit button on the Invoicing card is disabled.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
